/* eslint-disable react/react-in-jsx-scope */
import { useTranslation } from "react-i18next";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

import usePostData from "@/hooks/usePostData";

import { langs } from "@/constants/constants";
import { ChevronDown, Handshake, Loader, Mail, Send } from "lucide-react";
import { useEffect, useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { sessionManager } from "@/broadcastChannel/sessionManager";
import { toast } from "@/components/ui/use-toast";
import type { CSSProperties } from "react";

type LoginCallBranding = {
  logoUrl?: string | null;
  theme?: {
    primary?: string;
    primaryForeground?: string;
    background?: string;
    foreground?: string;
    border?: string;
    muted?: string;
    mutedForeground?: string;
  } | null;
};

type LoginCallResponse = {
  success?: boolean;
  data?: any;
  branding?: LoginCallBranding | null;
};

const LoginCall = () => {
  const { t } = useTranslation("general");
  const { postData } = usePostData();
  const { i18n } = useTranslation();
  // const [counter, setCounter] = useState(10);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [email, setEmail] = useState("");
  const { link, slugName } = useParams();
  const [codeAccess, setCodeAccess] = useState("");
  const navigate = useNavigate();
  const [loadingCheck, setLoadingCheck] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [callForProject, setCallForProject] = useState(null);
  const [branding, setBranding] = useState<LoginCallBranding | null>(null);

  const resolvedLogo = branding?.logoUrl?.trim() ? branding.logoUrl : "/logo.svg";
  const theme = branding?.theme ?? null;
  const pageStyle = {
    "--primary": theme?.primary || "85 49% 58%",
    "--primary-foreground": theme?.primaryForeground || "210 40% 98%",
    "--border": theme?.border || "214.3 31.8% 91.4%",
    "--muted": theme?.muted || "0 0% 96.21%",
    "--muted-foreground": theme?.mutedForeground || "215.4 16.3% 46.9%",
  } as CSSProperties;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_ENDPOINT}/sub-check`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ link: link, slug: slugName }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json: LoginCallResponse = await response.json();

        if (json.success) {
          setLoadingCheck(false);
          setCallForProject(json.data ?? null);
          setBranding(json.branding ?? null);
        } else {
          navigate("/error");
        }
      } catch (err: any) {
        navigate("/error");
        console.log(err.message || "Unknown error");
      } finally {
        setLoadingCheck(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/authentificate_sub`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ link: link, code: codeAccess.trim() }),
        }
      );

      const json = await response.json().catch(() => null);

      if (!response.ok) {
        toast({
          title: "Erreur",
          description: json?.message || t("Une erreur est survenue"),
          duration: 4000,
        });
        setIsLoading(false);
        return;
      }

      localStorage.removeItem("user");
      localStorage.clear();
      sessionStorage.clear();

      if (json.success) {
        setLoadingCheck(false);
        localStorage.setItem("code", "Regenerate2024!");
        sessionStorage.setItem("jwtToken", json.token);
        sessionStorage.setItem("refreshToken", json.refresh_token);
        sessionStorage.setItem("subcontractorId", link);
        sessionStorage.setItem("dataCall", JSON.stringify(callForProject));
        sessionStorage.setItem("slugName", slugName);
        localStorage.setItem("isSubcontractor", "subcontractor");
        localStorage.setItem("link", link);
        localStorage.setItem("slugName", slugName);

        sessionManager.login({
          jwtToken: json.token,
          refreshToken: json.refresh_token,
          remember: false,
          subcontractorId: link,
          slugName: slugName,
          dataCall: JSON.stringify(callForProject)

        });
        window.location.href = "/submit/appels-a-projets";
      } else {
        setIsLoading(false);
        // navigate('/error');
      }
    } catch (err: any) {
      setIsLoading(false);
      console.log(err.message || "Unknown error");
    } finally {
      setLoadingCheck(false);
    }
  };
  const AccessRecover = async () => {
    const res = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/authentificate_sub_recover`,
      data: {
        link: window.location.pathname.split("/")[2],
        slug: window.location.pathname.split("/")[3],
      },
    });

    if (res === null || res.success === false) {
      alert(t("Une erreur est survenue, veuillez réessayer plus tard"));
      return;
    }
    else {
      console.log(res)
      setEmail(res.mail);
      setIsRedirecting(true);
      toast({
        title: "Success",
        description: t("Email de récupération envoyé avec succès"),
        duration: 4000,
      });
    }

  };

  return (
    <div className="container mb-8" style={pageStyle}>
      <nav className="relative flex h-16 items-center border-b md:h-24">
        <ul className="flex w-full items-center">
          <li>
            <div className="flex h-14 w-28 items-center justify-center md:h-20 md:w-40">
              {!loadingCheck && (
                <Link
                  to="https://verdeo.fr"
                  className="flex h-full w-full items-center justify-center"
                >
                  <img
                    src={resolvedLogo}
                    alt="logo"
                    className="max-h-full max-w-full object-contain"
                  />
                </Link>
              )}
            </div>
          </li>
          <li className="ml-auto mr-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="cursor-pointer">
                <div className="flex items-center">
                  {i18n === undefined ||
                    i18n === null ||
                    i18n.language === undefined ||
                    i18n.language === null
                    ? "FR"
                    : i18n.language.split("-")[0].toUpperCase()}

                  <ChevronDown className="size-4 opacity-50" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-[10px]">
                <DropdownMenuGroup className="w-[50px]">
                  {langs.map((lang, key) => (
                    <DropdownMenuItem
                      className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                      onClick={() => {
                        i18n.language !== lang && i18n.changeLanguage(lang);
                      }}
                      key={key}
                    >
                      {lang.toUpperCase()}
                      {/* <span className={"fi fi-"+(lang === "en" ? "gb" : lang)+(i18n.language === lang ? " opacity-70 cursor-not-allowed" : "")} style={{
                                        width: '45px',
                                        height: '25px',
                                        backgroundSize: 'contain',
                                    }}></span> */}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
          <li className="mr-4 text-right">
            <a
              href="https://verdeo.fr/contact/"
              className="group relative flex size-[42px] items-center justify-center rounded-full border border-foreground md:size-max md:border-0"
            >
              <span className="hidden text-xs md:block md:text-base">
                {t("Contactez-nous")}!{" "}
              </span>
              <span className="absolute left-0 top-full hidden h-px w-0 bg-primary transition-all group-hover:w-full md:block"></span>
              <Mail className="block stroke-1 md:hidden" />
            </a>
          </li>
        </ul>
      </nav>
      {loadingCheck == true ? (
        <UserCheckSkeleton />
      ) : (
        <>
          <div className="mx-auto mb-5 mt-8 flex flex-wrap lg:w-2/4">
            <Card className="border-border/80 shadow-sm">
              <CardHeader>
                <CardTitle>{t("Veuillez saisir le code accès")}</CardTitle>
                <CardDescription>
                  {isRedirecting ? (<div>{t("Un code d'accès a bien été envoyé à l'adresse")}  <span className="font-semibold">{email}</span>. <br /> {t("Veuillez vérifier votre boîte de réception (et vos spams si nécessaire).")}</div>) : t(
                    "Veuillez saisir le code d'accès afin d'acceder à votre programme. Si ne vous l'avez pas encore reçu, contactez-nous."
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center mb-4">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full border border-border bg-muted">
                    <Handshake size={50} className="text-primary" />
                  </div>
                </div>
                {/* <br/> */}
                <Input
                  placeholder={t("Code d'accès")}
                  className={"mt-3 w-full bg-white"}
                  onChange={(e) => {
                    setCodeAccess(e.target.value);
                  }}
                />
                <div className="my-5 w-full flex justify-end">

                  <Button className="bg-transparent text-muted-foreground underline hover:bg-transparent hover:text-primary" onClick={AccessRecover}>
                    {t("Code d'accès oublié ?")}
                  </Button>
                </div>
                <Button
                  onClick={() => {
                    handleSave();
                  }}
                  disabled={isLoading || codeAccess === ""}
                  className={"mt-3 w-full bg-primary text-primary-foreground hover:opacity-90"}
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      {t("Envoi en cours")}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {t("Vérifier le code d'accès")}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

          </div>

        </>
      )}
    </div>
  );
};

const UserCheckSkeleton = () => {
  return (
    <div className="p-4 border rounded-lg shadow-sm bg-white max-w-lg mx-auto mt-4">
      {/* Avatar skeleton */}
      <div className="flex justify-center mb-4">
        <Skeleton circle width={80} height={80} />
      </div>

      {/* Nom */}
      <div className="mb-2">
        <Skeleton height={20} width={`60%`} style={{ margin: "0 auto" }} />
      </div>

      {/* Email */}
      <div className="mb-2">
        <Skeleton height={16} width={`80%`} style={{ margin: "0 auto" }} />
      </div>

      {/* Autres infos */}
      <div className="mt-4 space-y-2">
        <Skeleton height={14} width={`90%`} />
        <Skeleton height={14} width={`85%`} />
        <Skeleton height={14} width={`80%`} />
      </div>

      {/* Bouton ou action */}
      <div className="mt-6">
        <Skeleton height={36} width={`100%`} borderRadius={8} />
      </div>
    </div>
  );
};

export default LoginCall;


